import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import Table from 'Components/Table';
import { Text, Box, Flex, Icon, ICON_LIST } from 'Components/Base';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import { MASTERY_LEVEL } from 'Domains/mastery';
import Tooltip from 'Components/Tooltip';

const TextWrapper = styled(Box)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
  font-weight: ${({ fontWeight }) => fontWeight ?? 'normal'};
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 12px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      padding: 36px 12px;
    }
  }
`;

const TableHead = props => <Text fontWeight={600} {...props} />;

export const LearningResultAttendanceTable = ({ data, maxHeight, ...props }) => {
  const { t } = useTranslation('learningResult');
  const theme = useContext(ThemeContext);

  const getTextColor = value => {
    if (value > 0) return theme.colors.passText;
    if (value < 0) return theme.colors.failText;
  };

  const columns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: 'classNo',
      width: 100,
      Cell: ({ cell: { row } }) => (
        <TextWrapper minWidth={'100px'}>
          {row?.original?.classNo == null
            ? 'ทุกห้อง'
            : `${row.original?.grade}/${row.original?.classNo}`}
        </TextWrapper>
      ),
    },
    {
      id: 'studentNo',
      Header: <TableHead>{t('NO')}</TableHead>,
      accessor: 'studentNo',
      width: 100,
      Cell: ({ cell: { value } }) => <TextWrapper>{value}</TextWrapper>,
    },
    {
      id: 'username',
      Header: <TableHead>{t('USERNAME')}</TableHead>,
      accessor: 'classroomUsername',
      width: 140,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'140px'}>{value}</TextWrapper>,
    },
    {
      id: 'displayName',
      Header: <TableHead>{t('DISPLAY_NAME')}</TableHead>,
      accessor: properties => properties.firstName + ' ' + properties.lastName,
      width: 250,
      Cell: ({ cell: { row } }) => (
        <TextWrapper style={{ textAlign: 'left' }}>
          {row?.original?.firstName} {row?.original?.lastName}
        </TextWrapper>
      ),
    },
    {
      id: 'egg',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('EGG')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <Text
                as="span"
                style={{
                  fontFamily: 'Noto Sans Thai',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {t('EGG_TOOLTIP')}
              </Text>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'egg',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper>{`${value ?? 0} 🥚`}</TextWrapper>,
    },
    {
      id: 'eggTotal',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('EGG_TOTAL')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <Text
                as="span"
                style={{
                  fontFamily: 'Noto Sans Thai',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {t('EGG_TOTAL_TOOLTIP')}
              </Text>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'eggTotal',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper>{`${value ?? 0} 🥚`}</TextWrapper>,
    },
    {
      id: 'growthRate',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('GROWTH_RATE')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    fontWeight: 700,
                  }}
                >
                  {t('GROWTH_RATE')}
                </Text>
                <br />
                <Text
                  as="span"
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t('GAIN_SCORE_CHART_TOOLTIP')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'growthRate',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => {
        if (value !== null) {
          const formatValue = `${_.round(value)}%`;
          return (
            <TextWrapper color={getTextColor(value)}>
              {value > 0 ? `+${formatValue}` : formatValue}
            </TextWrapper>
          );
        } else {
          return null;
        }
      },
    },
    {
      id: 'mastery',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('MASTERY')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  as="span"
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t('MASTERY_CHART_TOOLTIP')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'mastery',
      disableSortBy: true,
      width: 200,
      Cell: ({ cell: { value } }) => {
        const masteryResult = MASTERY_LEVEL[value] ?? {
          name: t('NO_MASTERY_DATA'),
          color: 'transparent',
        };
        return (
          <Flex style={{ alignItems: 'center', justifyContent: 'center' }}>
            <Box
              style={{
                backgroundColor: masteryResult.color,
                width: '14px',
                height: '14px',
                flexShrink: 0,
                borderRadius: '50%',
              }}
            />
            <TextWrapper style={{ marginLeft: '8px' }}>{masteryResult.name}</TextWrapper>
          </Flex>
        );
      },
    },
    {
      id: 'bloom',
      Header: (
        <Flex style={{ alignItems: 'center' }}>
          <TableHead>{t('BLOOM')}</TableHead>
          <Tooltip
            arrow
            placement="top-end"
            title={
              <>
                <Text
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    fontWeight: 700,
                  }}
                >
                  {t('BLOOM')}
                </Text>
                <br />
                <Text
                  as="span"
                  style={{
                    fontFamily: 'Noto Sans Thai',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t('BLOOM_CHART_TOOLTIP')}
                </Text>
              </>
            }
          >
            <Box>
              <Icon
                name={ICON_LIST.INFO_CIRCLE}
                size={16}
                color="white"
                style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
              />
            </Box>
          </Tooltip>
        </Flex>
      ),
      accessor: 'bloom',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => {
        return <TextWrapper>{value || '-'}</TextWrapper>;
      },
    },
  ];

  return (
    <AttendanceTableStyle
      dense
      stickyHeader
      columns={columns}
      data={data}
      style={{ maxHeight }}
      {...props}
    />
  );
};

LearningResultAttendanceTable.propTypes = {
  data: PropTypes.array,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  schoolGrade: PropTypes.string,
};

export default LearningResultAttendanceTable;
